function safe(value) {
    const s = typeof value === 'string' ? value.trim() : '';
    return s.length > 0 ? s : 'Unknown';
}
function nodeId(parts) {
    return parts.join('::');
}
export function buildStoreHierarchyTree(stores, options) {
    const levels = options.levels ?? ['brand', 'businessUnit', 'country', 'aisOrganization', 'region', 'market', 'area', 'store'];
    const root = {
        id: 'root',
        level: 'brand',
        label: options.rootLabel ?? 'All',
        count: stores.length,
        children: [],
    };
    const byId = new Map([['root', root]]);
    for (const store of stores) {
        let parentId = root.id;
        for (const level of levels) {
            if (level === 'store') {
                const storeKey = safe(options.storeId(store));
                const id = nodeId([parentId, 'store', storeKey]);
                if (!byId.has(id)) {
                    const leaf = {
                        id,
                        level: 'store',
                        label: options.storeLabel(store),
                        value: storeKey,
                        count: 1,
                    };
                    byId.set(id, leaf);
                    byId.get(parentId).children.push(leaf);
                }
                else {
                    byId.get(id).count += 1;
                }
                continue;
            }
            const raw = options.selectors[level](store);
            const value = safe(raw);
            const id = nodeId([parentId, level, value]);
            if (!byId.has(id)) {
                const displayLevel = options.levelLabels?.[level] ?? level;
                const node = {
                    id,
                    level,
                    label: `${displayLevel}: ${value}`,
                    value,
                    count: 0,
                    children: [],
                };
                byId.set(id, node);
                byId.get(parentId).children.push(node);
            }
            const node = byId.get(id);
            node.count += 1;
            parentId = id;
        }
    }
    const sort = (node) => {
        if (!node.children)
            return;
        node.children.sort((a, b) => {
            if (b.count !== a.count)
                return b.count - a.count;
            return a.label.localeCompare(b.label);
        });
        node.children.forEach(sort);
    };
    sort(root);
    return root;
}
export function filterStoresByScope(stores, selection, selectors) {
    if (selection.length === 0)
        return stores;
    return stores.filter((store) => {
        for (const s of selection) {
            const storeValue = safe(selectors[s.level](store));
            if (storeValue !== safe(s.value))
                return false;
        }
        return true;
    });
}
/**
 * Default hierarchy order (source of truth).
 * Tweak this list to adjust the global hierarchy across apps.
 */
export const DEFAULT_STORE_HIERARCHY_LEVELS = [
    'brand',
    'businessUnit',
    'country',
    'aisOrganization',
    'region',
    'market',
    'area',
    'store',
];
export const DEFAULT_STORE_HIERARCHY_ROOT_LABEL = 'Yum!';
export const DEFAULT_STORE_HIERARCHY_LEVEL_LABELS = {
    brand: 'Brand',
    businessUnit: 'BMU',
    country: 'Country',
    aisOrganization: 'AIS Org',
    region: 'Region',
    market: 'Market',
    area: 'Area',
};
/**
 * Canonical hierarchy config for this repo.
 * The web UI (and any future consumers) should use this instead of duplicating hierarchy logic.
 */
export function createMappStoreHierarchyOptions(overrides) {
    return {
        levels: overrides?.levels ?? DEFAULT_STORE_HIERARCHY_LEVELS,
        rootLabel: DEFAULT_STORE_HIERARCHY_ROOT_LABEL,
        levelLabels: DEFAULT_STORE_HIERARCHY_LEVEL_LABELS,
        selectors: {
            brand: (s) => s.brand,
            businessUnit: (s) => s.businessUnitId,
            country: (s) => s.country,
            aisOrganization: (s) => s.aisOrganizationId,
            region: (s) => s.regionName ?? s.regionId,
            market: (s) => s.marketId,
            area: (s) => s.areaId,
        },
        storeId: (s) => s.internalStoreNumber,
        storeLabel: (s) => `${safe(s.storeName)} (#${safe(s.internalStoreNumber)})`,
    };
}
